/* eslint-disable react/no-multi-comp */
/* eslint-disable max-lines */
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFormik } from "formik";
import moment from "moment";
import React from "react";
import DatePicker, { ReactDatePickerCustomHeaderProps } from "react-datepicker";
import Select from "react-select";
import { ModalBody } from "reactstrap";
import * as Yup from "yup";

import { exportAllOrder } from "src/store/actions/admin";
import { useReducerData, useStoreActions } from "src/store/hooks";
import {
  ExportOrderPayload,
  OrderExportOption,
  exportOrderOptions,
} from "src/utils/constants";
import Button from "../Button";
import Modal from "../Modal";
import Typography from "../Typography";
// eslint-disable-next-line no-restricted-imports
import "react-datepicker/dist/react-datepicker.css";
import classes from "./styles.module.scss";
import { ExportOrderModalProps, StatusOption } from "./types";

const customStyles = {
  option: (provided: any, state: any) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    cursor: "pointer",
    backgroundColor: state.isFocused ? "#F8F9FA" : "white",
    color: "#212529",
    fontSize: "14px",
    lineHeight: "1.29",
    "&:hover": {
      backgroundColor: "#F8F9FA",
    },
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    minHeight: "46px",
    border: state.isFocused ? "1px solid #465365" : "1px solid #DEE2E6",
    boxShadow: state.isFocused ? "3px 4px 8px rgba(94, 114, 228, 0.1)" : "none",
    "&:hover": {
      borderColor: state.isFocused ? "#465365" : "#DEE2E6",
    },
  }),
  placeholder: (provided: any) => ({
    ...provided,
    color: "#212529",
    fontSize: "14px",
    lineHeight: "1.29",
  }),
  multiValue: (provided: any) => ({
    ...provided,
    backgroundColor: "#F8F9FA",
    borderRadius: "4px",
    margin: "2px",
  }),
  multiValueLabel: (provided: any) => ({
    ...provided,
    color: "#212529",
    fontSize: "14px",
    padding: "2px 8px",
  }),
  multiValueRemove: (provided: any) => ({
    ...provided,
    color: "#212529",
    "&:hover": {
      backgroundColor: "#DEE2E6",
      color: "#212529",
    },
  }),
};

const validationSchema = Yup.object().shape({
  exportType: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.object()
        .shape({
          report_type: Yup.string().required(),
          format: Yup.string().required(),
        })
        .required(),
    })
    .required("Export type is required")
    .nullable(),
  startDate: Yup.date().nullable().required("Start date is required"),
  endDate: Yup.date()
    .nullable()
    .required("End date is required")
    .test(
      "after-start-date",
      "End date must be after start date",
      function (value) {
        const { startDate } = this.parent;
        if (!startDate || !value) return true; // Skip validation if either date is not set
        return value > startDate;
      }
    ),
  statuses: Yup.array()
    .min(1, "At least one status must be selected")
    .required("Statuses are required"),
});

const formatDate = (date: Date) => {
  return moment(date).format("YYYY-MM-DD");
};

const DatePickerAny = DatePicker as any;

const ExportOrderModal: React.FC<ExportOrderModalProps> = ({
  isOpen,
  toggle,
  orderStatuses,
}) => {
  const actions = useStoreActions({
    exportAllOrder,
  });
  const { loading } = useReducerData("admin", "exportAllOrder", { data: [] });

  const SELECT_ALL_VALUE = -1;
  const showMaxYears = 25;
  const statusOptions: StatusOption[] = orderStatuses
    ? [
        { value: SELECT_ALL_VALUE, label: "Select All" },
        ...orderStatuses.map((status) => ({
          value: status.id,
          label: status.name,
        })),
      ]
    : [];

  const regularStatusOptions = statusOptions.filter(
    (option) => option.value !== SELECT_ALL_VALUE
  );

  const formik = useFormik({
    initialValues: {
      startDate: null,
      endDate: null,
      statuses: [] as StatusOption[],
      exportType: null as OrderExportOption | null,
    },
    validationSchema,
    validateOnMount: false,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: (values) => {
      const filteredStatuses = values.statuses.filter(
        (option) => option.value !== SELECT_ALL_VALUE
      );
      handleExport({
        start_date: values.startDate
          ? formatDate(values.startDate as Date)
          : null,
        end_date: values.endDate ? formatDate(values.endDate as Date) : null,
        statuses: filteredStatuses.map((option) => option.value),
        format: values.exportType?.value.format || "csv",
        report_type: values.exportType?.value.report_type || "all",
      });
    },
  });

  const formatOptionLabel = (option: OrderExportOption) => {
    const label = option.label;
    const formatIndex = label.lastIndexOf("(");

    if (formatIndex === -1) return label;

    const baseText = label.substring(0, formatIndex);
    const formatText = label.substring(formatIndex);

    return (
      <div className={classes.optionLabel}>
        <span>{baseText}</span>
        <span className={classes.format}>{formatText}</span>
      </div>
    );
  };

  const renderCustomHeader = ({
    date,
    changeYear,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }: ReactDatePickerCustomHeaderProps) => (
    <div className={classes.customHeader}>
      <button
        type="button"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        className={classes.navButton}
      >
        <i className="fa fa-chevron-left" />
      </button>
      <div className={classes.monthYearContainer}>
        <span className={classes.currentMonth}>
          {date.toLocaleString("default", { month: "long" })}
        </span>
        <select
          value={date.getFullYear()}
          onChange={({ target: { value } }) => changeYear(Number(value))}
          className={classes.yearDropdown}
        >
          {Array.from(
            { length: showMaxYears },
            (_, i) => new Date().getFullYear() - i
          ).map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>
      <button
        type="button"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        className={classes.navButton}
      >
        <i className="fa fa-chevron-right" />
      </button>
    </div>
  );

  const handleExport = async (data: ExportOrderPayload) => {
    const { format } = data;
    const reports = (await actions.exportAllOrder(data)) as any;
    const url = window.URL.createObjectURL(new Blob([reports.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `orders.${format}`);
    document.body.appendChild(link);
    link.click();
  };

  const areAllOptionsSelected =
    formik.values.statuses.length === regularStatusOptions.length &&
    regularStatusOptions.every((option) =>
      formik.values.statuses.some((selected) => selected.value === option.value)
    );

  return (
    <Modal className={classes.modal} isOpen={isOpen} toggle={toggle}>
      <ModalBody className={classes.modalBody}>
        <Typography variant="h3" fontWeight="bold">
          Export Orders
        </Typography>
        <Typography className={classes.description}>
          Select the date range and statuses for the orders you'd like to export
          from the system.
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <div className={classes.selectWrapper}>
            <label className={classes.dateLabel}>
              Export Type<span className={classes.required}>*</span>
            </label>
            <Select
              value={formik.values.exportType}
              onChange={(selected) => {
                formik.setFieldValue("exportType", selected);
              }}
              onBlur={() => formik.setFieldTouched("exportType", true)}
              options={exportOrderOptions}
              placeholder="Select Export Type"
              classNamePrefix="type-select"
              className={`${classes.statusSelect} ${
                formik.touched.exportType && formik.errors.exportType
                  ? classes.error
                  : ""
              }`}
              styles={customStyles}
              formatOptionLabel={formatOptionLabel}
              maxMenuHeight={500}
            />
            {formik.touched.exportType && formik.errors.exportType && (
              <div className={classes.errorText}>
                {formik.errors.exportType as string}
              </div>
            )}
          </div>
          <div className={classes.dateContainer}>
            <div className={classes.dateInput}>
              <label className={classes.dateLabel}>
                Start Date<span className={classes.required}>*</span>
              </label>
              <div className={classes.datePickerWrapper}>
                <DatePickerAny
                  selected={formik.values.startDate}
                  onChange={(date: Date | null) => {
                    formik.setFieldValue("startDate", date);
                  }}
                  onBlur={() => formik.setFieldTouched("startDate", true)}
                  placeholderText="Select Date"
                  className={`${classes.datePicker} ${
                    formik.touched.startDate && formik.errors.startDate
                      ? classes.error
                      : ""
                  }`}
                  dateFormat="MMMM dd, yyyy"
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={10}
                  maxDate={new Date()}
                  popperClassName={classes.datePickerPopper}
                  renderCustomHeader={renderCustomHeader}
                />
                <FontAwesomeIcon
                  icon={faCalendar}
                  className={classes.calendarIcon}
                />
              </div>
              {formik.touched.startDate && formik.errors.startDate && (
                <div className={classes.errorText}>
                  {formik.errors.startDate}
                </div>
              )}
            </div>
            <div className={classes.dateInput}>
              <label className={classes.dateLabel}>
                End Date<span className={classes.required}>*</span>
              </label>
              <div className={classes.datePickerWrapper}>
                <DatePickerAny
                  selected={formik.values.endDate}
                  onChange={(date: Date | null) => {
                    formik.setFieldValue("endDate", date);
                  }}
                  onBlur={() => formik.setFieldTouched("endDate", true)}
                  placeholderText="Select Date"
                  className={`${classes.datePicker} ${
                    formik.touched.endDate && formik.errors.endDate
                      ? classes.error
                      : ""
                  }`}
                  dateFormat="MMMM dd, yyyy"
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={10}
                  renderCustomHeader={renderCustomHeader}
                  maxDate={new Date()} // Added maxDate to prevent future date selection
                />
                <FontAwesomeIcon
                  icon={faCalendar}
                  className={classes.calendarIcon}
                />
              </div>
              {formik.touched.endDate && formik.errors.endDate && (
                <div className={classes.errorText}>{formik.errors.endDate}</div>
              )}
            </div>
          </div>
          <div className={classes.statusContainer}>
            <div className={classes.selectWrapper}>
              <label className={classes.dateLabel}>
                Statuses<span className={classes.required}>*</span>
              </label>
              <Select
                isMulti
                value={formik.values.statuses}
                onChange={(selected) => {
                  // Check if the Select All option was clicked
                  const selectAllOption = selected.find(
                    (option) => option.value === SELECT_ALL_VALUE
                  );
                  const wasSelectAllPreviouslySelected =
                    selected.length === regularStatusOptions.length;
                  // Case 1: Select All was clicked (and wasn't fully selected before)
                  if (selectAllOption && !wasSelectAllPreviouslySelected) {
                    // Select all regular options
                    formik.setFieldValue("statuses", regularStatusOptions);
                    return;
                  }

                  // Case 2: "Deselect All" was clicked (all were previously selected)
                  if (!selectAllOption && wasSelectAllPreviouslySelected) {
                    // Clear all selections
                    formik.setFieldValue("statuses", []);
                    return;
                  }

                  // Case 3: Regular selection behavior
                  const newSelection = selected.filter(
                    (option) => option.value !== SELECT_ALL_VALUE
                  );
                  formik.setFieldValue("statuses", newSelection);
                }}
                onBlur={() => formik.setFieldTouched("statuses", true)}
                options={[
                  // Dynamically change the label based on selection state
                  {
                    value: SELECT_ALL_VALUE,
                    label: areAllOptionsSelected
                      ? "Deselect All Statuses"
                      : "Select All Statuses",
                  },
                  ...regularStatusOptions,
                ]}
                placeholder="Select Statuses"
                className={`${classes.statusSelect} ${
                  formik.touched.statuses && formik.errors.statuses
                    ? classes.error
                    : ""
                }`}
                hideSelectedOptions={false}
                classNamePrefix="status-select"
                styles={customStyles}
                closeMenuOnSelect={false}
                isOptionSelected={(option) => {
                  if (option.value === SELECT_ALL_VALUE) {
                    return areAllOptionsSelected;
                  }
                  return formik.values.statuses.some(
                    (selected) => selected.value === option.value
                  );
                }}
                components={{
                  Option: ({ children, ...props }: any) => {
                    const isSelectAll = props.data.value === SELECT_ALL_VALUE;

                    return (
                      <div
                        {...props.innerProps}
                        style={{
                          ...props.getStyles("option", props),
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {!isSelectAll && (
                            <div
                              style={{
                                width: "18px",
                                height: "18px",
                                border: props.isSelected
                                  ? "2px solid #465365"
                                  : "2px solid #DEE2E6",
                                borderRadius: "4px",
                                marginRight: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: props.isSelected
                                  ? "#465365"
                                  : "transparent",
                              }}
                            />
                          )}
                          <div>{children}</div>
                        </div>
                      </div>
                    );
                  },
                }}
              />
              {formik.touched.statuses && formik.errors.statuses && (
                <div className={classes.errorText}>
                  {formik.errors.statuses as string}
                </div>
              )}
            </div>
          </div>
          <div className={classes.footer}>
            <Button
              buttonText="Cancel"
              variant="link"
              onClick={toggle}
              className={classes.footerButton}
              type="button"
            />
            <Button
              buttonText="Export Orders"
              type="submit"
              loading={loading}
              color="primary"
              className={classes.footerButton}
              disabled={loading || !formik.isValid}
            />
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default ExportOrderModal;
